// Detects when this bundle is older than the release the server is running.
//
// This is the proactive half of stale-tab recovery: the server stamps every
// /api response with an X-App-Version header (server/harness.js), we compare it
// against the version baked into this bundle, and reload immediately on
// mismatch — before the tab tries to load a chunk that no longer exists.
// util/chunkReload.js is the reactive half, for when it does.
//
// Known blind spot (CH-460): the header carries package.json's version, so two
// separately-built deploys of the *same* version serving side by side compare
// equal and nothing here fires. Detecting that needs a per-build id, not a
// release number.

const bundleVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;

let serverVersion = null;

export const isBundleStale = () =>
  !!(bundleVersion && serverVersion && serverVersion !== bundleVersion);

const VERSION_RELOAD_KEY = 'version-reload';

// See CHUNK_RELOAD_STORAGE_KEYS in util/chunkReload.js — the logout sweep in
// reducers.js skips these so it can't re-arm a reload budget on a wedged tab.
export const APP_VERSION_STORAGE_KEYS = [VERSION_RELOAD_KEY];

// True once a reload/redirect has been issued: the page keeps executing until
// the browser tears it down, and the splash gate must stay up for that window
// instead of fading out to a flash of stale content.
let reloadInFlight = false;
export const isReloadInFlight = () => reloadInFlight;

// Only one attempt per server version: if the mismatch persists after a fresh
// load (e.g. a misconfigured header), we must not keep forcing full loads.
const claimReloadFor = (version) => {
  // With storage blocked (e.g. cookies disabled — even *reading* sessionStorage
  // throws then), the one-attempt guard has nowhere to live, so we can't rule
  // out a reload loop. Never reload those tabs: they just stay on the old
  // bundle until a manual refresh, which is the pre-header behaviour anyway.
  // A throw must not escape — this runs inside the axios success interceptor,
  // where it would turn every successful response into a rejection.
  try {
    if (sessionStorage.getItem(VERSION_RELOAD_KEY) === version) {
      return false;
    }
    sessionStorage.setItem(VERSION_RELOAD_KEY, version);
  } catch {
    return false;
  }
  // eslint-disable-next-line no-console
  console.log(
    `[version-reload] bundle ${bundleVersion} is stale (server on ${version}), reloading`
  );
  return true;
};

// Called from the axios response interceptor in util/api.js — runs on both
// success and error responses, and also during SSR (hence the window guard).
// Reloads on the spot when stale — but never off a mutation response, since a
// reload straight after e.g. initiate-privileged could swallow a payment
// redirect; those cases are picked up by the next GET or route navigation.
export const observeVersionHeader = (headers, requestMethod) => {
  const version =
    typeof headers?.get === 'function' ? headers.get('x-app-version') : headers?.['x-app-version'];
  if (version) {
    serverVersion = version;
  }

  const isGet = !requestMethod || requestMethod.toLowerCase() === 'get';
  if (isGet && typeof window !== 'undefined' && isBundleStale() && claimReloadFor(serverVersion)) {
    reloadInFlight = true;
    // reload() (vs assign) keeps the URL and lets the browser restore scroll.
    window.location.reload();
  }
};

// Fallback for staleness observed on a mutation response: replaces the SPA
// navigation with a full page load to the destination.
export const reloadIfStale = (href) => {
  if (!isBundleStale()) return false;
  if (!claimReloadFor(serverVersion)) return false;

  reloadInFlight = true;
  window.location.assign(href || window.location.href);
  return true;
};
